/* Linsoul product sound graph: shared 1000x500 frame, log 20 Hz–20 kHz, 10 dB divisions, 3px cyan trace. No dependencies. */
(() => {
  'use strict';

  const NS = 'http://www.w3.org/2000/svg';
  const W = 1000;
  const H = 500;
  const F_MIN = 20;
  const F_MAX = 20000;
  const LOG_MIN = Math.log10(F_MIN);
  const LOG_SPAN = Math.log10(F_MAX) - LOG_MIN;
  const MAJOR = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  const NARROW_MAJOR = [20, 100, 1000, 10000, 20000];
  const COLORS = { surface: '#111214', minor: 'rgba(255,255,255,0.07)', major: 'rgba(255,255,255,0.18)', text: '#a7aab0', trace: '#00FFFF' };

  const fmtFreq = (f) => (f >= 1000 ? `${(f / 1000).toFixed(f >= 10000 ? 1 : 2)} kHz` : `${Math.round(f)} Hz`);
  const tickLabel = (f) => (f >= 1000 ? `${f / 1000}k` : String(f));
  const el = (name, attrs, text) => {
    const node = document.createElementNS(NS, name);
    Object.entries(attrs || {}).forEach(([k, v]) => node.setAttribute(k, v));
    if (text != null) node.textContent = text;
    return node;
  };

  function valueAt(points, f) {
    let lo = 0;
    let hi = points.length - 1;
    if (f <= points[0][0]) return points[0][1];
    if (f >= points[hi][0]) return points[hi][1];
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (points[mid][0] < f) lo = mid;
      else hi = mid;
    }
    const [f0, v0] = points[lo];
    const [f1, v1] = points[hi];
    const t = (Math.log(f) - Math.log(f0)) / (Math.log(f1) - Math.log(f0));
    return v0 + (v1 - v0) * t;
  }

  function nearestIndex(points, f) {
    let lo = 0;
    let hi = points.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (points[mid][0] < f) lo = mid;
      else hi = mid;
    }
    return Math.abs(Math.log(points[lo][0] / f)) <= Math.abs(Math.log(points[hi][0] / f)) ? lo : hi;
  }

  function initGraph(root) {
    let data;
    try {
      data = JSON.parse(root.querySelector('[data-lss-data]').textContent);
    } catch (error) {
      console.warn('Linsoul measurement unreadable', error);
      return;
    }
    const source = (Array.isArray(data.points) ? data.points : [])
      .map((p) => [Number(p[0]), Number(p[1])])
      .filter(([f, v]) => Number.isFinite(f) && Number.isFinite(v) && f > 0)
      .sort((a, b) => a[0] - b[0]);
    if (source.length < 2) {
      root.hidden = true;
      return;
    }

    const plot = root.querySelector('[data-lss-plot]');
    const tooltip = root.querySelector('[data-lss-tooltip]');
    const L = root.dataset;
    const fontFamily = getComputedStyle(root).fontFamily || 'sans-serif';
    const num = (v, d) => (v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : d);
    const NORM = {
      on: L.normalize !== 'false',
      hz: num(L.normalizeHz, 500),
      db: num(L.normalizeDb, 65),
      min: num(L.axisMin, 40),
      max: num(L.axisMax, 90),
    };
    let mode = NORM.on ? 'aligned' : 'raw';
    let svg = null;
    let geometry = null;
    let readingIndex = -1;

    const count = root.querySelector('[data-lss-count]');
    if (count) count.textContent = String(source.length);
    const range = root.querySelector('[data-lss-range]');
    if (range) range.textContent = `${fmtFreq(source[0][0])} – ${fmtFreq(source[source.length - 1][0])}`;

    const displayed = () => {
      if (mode !== 'aligned') return source;
      const band = source.filter(([f]) => f >= NORM.hz / 1.26 && f <= NORM.hz * 1.26);
      const level = band.length ? band.reduce((sum, p) => sum + p[1], 0) / band.length : valueAt(source, NORM.hz);
      const offset = NORM.db - level;
      return source.map(([f, v]) => [f, v + offset]);
    };

    function render() {
      const width = plot.clientWidth || W;
      const scale = W / width;
      const fs = Math.max(13, 11.5 * scale);
      const narrow = width < 560;
      const M = { l: fs * 4.6, r: fs * 1.2, t: fs * 1.1, b: fs * 3.6 };
      const pts = displayed().filter(([f]) => f >= F_MIN && f <= F_MAX);
      const values = pts.map((p) => p[1]);
      let top;
      let bottom;
      if (mode === 'aligned' && NORM.max > NORM.min) {
        top = NORM.max;
        bottom = NORM.min;
        while (Math.max(...values) + 2 > top) top += 10;
        while (Math.min(...values) - 2 < bottom) bottom -= 10;
      } else {
        top = Math.ceil((Math.max(...values) + 3) / 10) * 10;
        bottom = top - Math.max(50, Math.ceil((top - Math.min(...values) + 5) / 10) * 10);
      }
      const span = top - bottom;
      const x = (f) => M.l + ((Math.log10(f) - LOG_MIN) / LOG_SPAN) * (W - M.l - M.r);
      const y = (v) => M.t + ((top - v) / span) * (H - M.t - M.b);
      geometry = { M, x, y, top, bottom, pts };

      const next = el('svg', { viewBox: `0 0 ${W} ${H}`, width: '100%', role: 'presentation', 'font-family': fontFamily });
      next.append(el('rect', { x: 0, y: 0, width: W, height: H, fill: COLORS.surface, rx: 8 * scale }));
      const grid = el('g', { 'shape-rendering': 'crispEdges' });
      for (let decade = 10; decade <= 10000; decade *= 10) {
        for (let k = 1; k <= 9; k += 1) {
          const f = decade * k;
          if (f < F_MIN || f > F_MAX || MAJOR.includes(f)) continue;
          grid.append(el('line', { x1: x(f), x2: x(f), y1: M.t, y2: H - M.b, stroke: COLORS.minor, 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
        }
      }
      const labels = el('g', { fill: COLORS.text, 'font-size': fs });
      MAJOR.forEach((f) => {
        grid.append(el('line', { x1: x(f), x2: x(f), y1: M.t, y2: H - M.b, stroke: COLORS.major, 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
        if (!narrow || NARROW_MAJOR.includes(f)) {
          const anchor = f === F_MIN ? 'start' : f === F_MAX ? 'end' : 'middle';
          labels.append(el('text', { x: x(f), y: H - M.b + fs * 1.45, 'text-anchor': anchor }, tickLabel(f)));
        }
      });
      for (let v = bottom; v <= top; v += 10) {
        grid.append(el('line', { x1: M.l, x2: W - M.r, y1: y(v), y2: y(v), stroke: v === bottom || v === top ? COLORS.major : COLORS.minor, 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
        labels.append(el('text', { x: M.l - fs * 0.6, y: y(v) + fs * 0.35, 'text-anchor': 'end' }, String(v)));
      }
      labels.append(el('text', { x: M.l + (W - M.l - M.r) / 2, y: H - fs * 0.7, 'text-anchor': 'middle' }, L.labelX));
      labels.append(el('text', { x: fs * 1.1, y: M.t + (H - M.t - M.b) / 2, 'text-anchor': 'middle', transform: `rotate(-90 ${fs * 1.1} ${M.t + (H - M.t - M.b) / 2})` }, L.labelY));
      next.append(grid, labels);

      const d = pts.map(([f, v], i) => `${i ? 'L' : 'M'}${x(f).toFixed(1)} ${y(v).toFixed(1)}`).join('');
      next.append(el('path', { d, fill: 'none', stroke: COLORS.trace, 'stroke-width': 3, 'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }));
      const marker = el('circle', { r: 6 * Math.max(1, scale * 0.7), fill: COLORS.surface, stroke: COLORS.trace, 'stroke-width': 2, 'vector-effect': 'non-scaling-stroke', visibility: 'hidden', 'data-lss-marker': '' });
      next.append(marker);

      if (svg) svg.replaceWith(next);
      else plot.prepend(next);
      svg = next;
      if (readingIndex >= 0) showReading(readingIndex);
    }

    function showReading(index) {
      if (!geometry || !geometry.pts.length) return;
      const pts = geometry.pts;
      readingIndex = Math.max(0, Math.min(pts.length - 1, index));
      const [f, v] = pts[readingIndex];
      const cx = geometry.x(f);
      const cy = geometry.y(v);
      const marker = svg.querySelector('[data-lss-marker]');
      marker.setAttribute('cx', cx);
      marker.setAttribute('cy', cy);
      marker.setAttribute('visibility', 'visible');
      tooltip.textContent = (L.labelReading || '[freq] · [db] dB').replace('[freq]', fmtFreq(f)).replace('[db]', v.toFixed(1));
      tooltip.hidden = false;
      tooltip.style.left = `${(cx / W) * 100}%`;
      tooltip.style.top = `${(cy / H) * 100}%`;
      tooltip.dataset.side = cx > W * 0.7 ? 'left' : 'right';
    }

    function hideReading() {
      readingIndex = -1;
      tooltip.hidden = true;
      svg?.querySelector('[data-lss-marker]')?.setAttribute('visibility', 'hidden');
    }

    plot.addEventListener('pointermove', (event) => {
      if (!geometry) return;
      const box = svg.getBoundingClientRect();
      const vx = ((event.clientX - box.left) / box.width) * W;
      const { M } = geometry;
      if (vx < M.l || vx > W - M.r) return hideReading();
      const f = 10 ** (LOG_MIN + ((vx - M.l) / (W - M.l - M.r)) * LOG_SPAN);
      showReading(nearestIndex(geometry.pts, f));
    });
    plot.addEventListener('pointerleave', hideReading);
    plot.addEventListener('blur', hideReading);
    plot.addEventListener('keydown', (event) => {
      if (!geometry) return;
      const pts = geometry.pts;
      const current = readingIndex >= 0 ? pts[readingIndex][0] : 1000;
      let f = null;
      if (event.key === 'ArrowRight') f = current * 2 ** (1 / 6);
      if (event.key === 'ArrowLeft') f = current / 2 ** (1 / 6);
      if (event.key === 'Home') f = pts[0][0];
      if (event.key === 'End') f = pts[pts.length - 1][0];
      if (event.key === 'Escape') return hideReading();
      if (f == null) return;
      event.preventDefault();
      showReading(nearestIndex(pts, f));
    });
    plot.addEventListener('focus', () => {
      if (readingIndex < 0 && geometry) showReading(nearestIndex(geometry.pts, 1000));
    });

    const note = root.querySelector('[data-lss-level-note]');
    root.querySelectorAll('[data-lss-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lssMode === mode)));
    if (note) note.textContent = mode === 'aligned' ? L.labelAlignedNote : L.labelRawNote;
    root.querySelectorAll('[data-lss-mode]').forEach((button) => button.addEventListener('click', () => {
      mode = button.dataset.lssMode;
      root.querySelectorAll('[data-lss-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      if (note) note.textContent = mode === 'aligned' ? L.labelAlignedNote : L.labelRawNote;
      render();
    }));

    const save = (blob, name) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    root.querySelector('[data-lss-download]')?.addEventListener('click', () => {
      const rows = displayed().map(([f, v]) => `${f},${v}`);
      const header = [`# ${data.label || ''}`, `# ${data.source || ''}`, `# ${mode === 'aligned' ? L.labelAlignedNote : L.labelRawNote}`, `${L.labelX},${L.labelY}`];
      save(new Blob([[...header, ...rows].join('\n')], { type: 'text/csv' }), `${L.fileName}.csv`);
    });
    root.querySelector('[data-lss-export]')?.addEventListener('click', () => {
      const copy = svg.cloneNode(true);
      copy.setAttribute('xmlns', NS);
      copy.setAttribute('width', W);
      copy.setAttribute('height', H);
      copy.querySelector('[data-lss-marker]')?.remove();
      copy.prepend(el('desc', {}, `${data.source || ''} — ${mode === 'aligned' ? L.labelAlignedNote : L.labelRawNote}`));
      copy.prepend(el('title', {}, data.label || ''));
      save(new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml' }), `${L.fileName}.svg`);
    });

    let lastWidth = 0;
    new ResizeObserver(() => {
      const width = plot.clientWidth;
      if (Math.abs(width - lastWidth) < 4) return;
      lastWidth = width;
      render();
    }).observe(plot);
  }

  const init = () => document.querySelectorAll('[data-lss]').forEach((root) => {
    if (root.dataset.lssReady) return;
    root.dataset.lssReady = '1';
    initGraph(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
